<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue';
  import { useLocalStorage } from '@vueuse/core';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiPopover from '@/components/pos-v2/ui/PosUiPopover.vue';
  import V2StatsBusinessDayNote from '@/components/v2/stats/V2StatsBusinessDayNote.vue';
  import StatsService from '@/api/services/stats.service';
  import { IStatsContext } from '@/interfaces/IStatsContext';
  import { hasBusinessDayCutoff } from '@/domain/stats/business-day';
  import {
    DEFAULT_STATS_RANGE_PERSISTED,
    IStatsDateRange,
    IStatsRangePersisted,
    STATS_RANGE_PRESETS,
    TStatsRangePreset,
    getStatsCustomRange,
    getStatsPresetRange,
    parseLocalDayInput,
    resolveStatsRange,
    toDateInputValue,
  } from '@/domain/stats/stats-date-range';

  const props = withDefaults(
    defineProps<{
      restaurantId: number;
      branchId: number;
      /** Namespaces the localStorage persistence; shared by default. */
      storageKey?: string;
      /**
       * Hides the business/calendar toggle and forces business mode — for
       * pages backed by business-day-native endpoints (day closes).
       */
      businessOnly?: boolean;
    }>(),
    { storageKey: 'default', businessOnly: false }
  );

  // 'change' (not 'input'): the control owns its state (localStorage), so it
  // deliberately does NOT implement the v-model value/input contract.
  const emit = defineEmits<{
    (e: 'change', value: IStatsDateRange): void;
    (e: 'context', value: IStatsContext): void;
  }>();

  const statsService = new StatsService();

  const context = ref<IStatsContext | null>(null);
  /** Branch the loaded context belongs to (lags props.branchId on switches). */
  const contextBranchId = ref<number | null>(null);
  const persisted = useLocalStorage<IStatsRangePersisted>(
    `v2-stats-range:${props.storageKey}`,
    { ...DEFAULT_STATS_RANGE_PERSISTED },
    { mergeDefaults: true }
  );

  // Custom popover state (kept separate from the applied range).
  const customFromInput = ref('');
  const customToInput = ref('');

  /** Active mode; businessOnly pages ignore any persisted calendar choice. */
  const calendarActive = computed(
    () => !props.businessOnly && persisted.value.calendar
  );

  /**
   * Business mode anchors "today" on the branch business date; calendar mode
   * (and the pre-context frame) on the browser's local day.
   */
  const anchorDay = computed(() => {
    if (!calendarActive.value && context.value) {
      const parsed = parseLocalDayInput(context.value.currentBusinessDate);
      if (parsed) return parsed;
    }
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });

  const currentRange = computed<IStatsDateRange>(() =>
    resolveStatsRange(
      { ...persisted.value, calendar: calendarActive.value },
      anchorDay.value
    )
  );

  const rangeLabel = computed(() => {
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
    const { from, to } = currentRange.value;
    return from.getTime() === to.getTime()
      ? fmt(from)
      : `${fmt(from)} – ${fmt(to)}`;
  });

  /**
   * Business-day note (cutoff > 0, business mode only). Hidden while a branch
   * switch is still resolving, so it always describes the current branch.
   */
  const businessNote = computed(() => {
    const ctx = context.value;
    const branchId = contextBranchId.value;
    return !calendarActive.value &&
      ctx &&
      branchId === props.branchId &&
      hasBusinessDayCutoff(ctx)
      ? { context: ctx, branchId }
      : null;
  });

  function emitRange() {
    emit('change', currentRange.value);
  }

  function selectPreset(preset: Exclude<TStatsRangePreset, 'custom'>) {
    persisted.value = { ...persisted.value, preset };
    emitRange();
  }

  function setCalendarMode(calendar: boolean) {
    if (persisted.value.calendar === calendar) return;
    persisted.value = { ...persisted.value, calendar };
    emitRange();
  }

  function openCustom(toggle: () => void) {
    const { from, to } = currentRange.value;
    customFromInput.value = toDateInputValue(from);
    customToInput.value = toDateInputValue(to);
    toggle();
  }

  const customValid = computed(
    () =>
      getStatsCustomRange(customFromInput.value, customToInput.value) !== null
  );

  function applyCustom(close: () => void) {
    const range = getStatsCustomRange(
      customFromInput.value,
      customToInput.value
    );
    if (!range) return;
    persisted.value = {
      ...persisted.value,
      preset: 'custom',
      customFrom: toDateInputValue(range.from),
      customTo: toDateInputValue(range.to),
    };
    emitRange();
    close();
  }

  // Guards out-of-order resolutions on rapid branch switches.
  let contextGeneration = 0;

  async function loadContext() {
    const generation = ++contextGeneration;
    const branchId = props.branchId;
    try {
      const ctx = await statsService.getContext(props.restaurantId, branchId);
      if (generation !== contextGeneration) return;
      context.value = ctx;
      contextBranchId.value = branchId;
      emit('context', ctx);
    } catch {
      // Business "today" falls back to the browser's local day; the pages
      // still work, only the anchored cutoff niceties are lost.
      if (generation === contextGeneration) context.value = null;
    }
  }

  onMounted(async () => {
    await loadContext();
    emitRange();
  });

  watch(
    () => props.branchId,
    async () => {
      await loadContext();
      emitRange();
    }
  );

  const presetLabels: Record<Exclude<TStatsRangePreset, 'custom'>, string> = {
    today: 'v2Stats.range.today',
    yesterday: 'v2Stats.range.yesterday',
    '7d': 'v2Stats.range.last7',
    '30d': 'v2Stats.range.last30',
    thisMonth: 'v2Stats.range.thisMonth',
    lastMonth: 'v2Stats.range.lastMonth',
  };

  const presets = STATS_RANGE_PRESETS;
</script>

<template>
  <div class="v2-stats-date-range">
    <div class="range-row">
      <div class="range-presets">
        <button
          v-for="preset in presets"
          :key="preset"
          type="button"
          class="preset-chip"
          :class="{ 'is-active': persisted.preset === preset }"
          @click="selectPreset(preset)"
        >
          {{ $t(presetLabels[preset]) }}
        </button>

        <PosUiPopover placement="bottom" align="end">
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="preset-chip"
              :class="{ 'is-active': persisted.preset === 'custom' }"
              @click="openCustom(toggle)"
            >
              {{
                persisted.preset === 'custom'
                  ? rangeLabel
                  : $t('v2Stats.range.custom')
              }}
            </button>
          </template>
          <template #default="{ close }">
            <div class="custom-panel">
              <label class="custom-field">
                <span class="custom-field-label">{{
                  $t('v2Stats.range.from')
                }}</span>
                <input
                  v-model="customFromInput"
                  type="date"
                  class="custom-field-input"
                />
              </label>
              <label class="custom-field">
                <span class="custom-field-label">{{
                  $t('v2Stats.range.to')
                }}</span>
                <input
                  v-model="customToInput"
                  type="date"
                  class="custom-field-input"
                />
              </label>
              <PosUiButton
                color="neutral"
                size="md"
                block
                :disabled="!customValid"
                @click="applyCustom(close)"
              >
                {{ $t('v2Stats.range.apply') }}
              </PosUiButton>
            </div>
          </template>
        </PosUiPopover>
      </div>

      <div v-if="!businessOnly" class="range-mode">
        <button
          type="button"
          class="mode-option"
          :class="{ 'is-active': !calendarActive }"
          @click="setCalendarMode(false)"
        >
          {{ $t('v2Stats.range.businessMode') }}
        </button>
        <button
          type="button"
          class="mode-option"
          :class="{ 'is-active': calendarActive }"
          @click="setCalendarMode(true)"
        >
          {{ $t('v2Stats.range.calendarMode') }}
        </button>
      </div>
    </div>

    <V2StatsBusinessDayNote
      v-if="businessNote"
      :context="businessNote.context"
      :branch-id="businessNote.branchId"
      class="range-footnote"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-stats-date-range {
    @apply tw-min-w-0 tw-max-w-full;

    .range-row {
      // items-start + uniform tw-mb-2 on every 44px control: aligned tops =
      // one shared baseline for the whole command bar (see V2ReportPage).
      @apply tw-flex tw-flex-wrap tw-items-start;
    }

    .range-presets {
      @apply tw-mr-3 tw-flex tw-flex-wrap tw-items-start;

      .preset-chip {
        // margins instead of flex gap: unsupported on Chrome <84.
        @apply tw-mb-2 tw-mr-1 tw-h-11 tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-neutral-100 tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-600 tw-transition-colors hover:tw-bg-neutral-200;

        &.is-active {
          @apply tw-bg-neutral-900 tw-text-white hover:tw-bg-neutral-900;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    // Card chrome (border/shadow/padding) comes from the popover panel.
    .custom-panel {
      .custom-field {
        @apply tw-mb-2 tw-block;

        .custom-field-label {
          @apply tw-mb-1 tw-block tw-text-xs tw-font-medium tw-text-neutral-500;
        }

        .custom-field-input {
          @apply tw-h-10 tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-neutral-300 tw-bg-white tw-px-2.5 tw-text-sm tw-text-neutral-900;

          &:focus {
            @apply tw-border-orange-500 tw-outline-none tw-ring-2 tw-ring-orange-500/40;
          }
        }
      }
    }

    .range-mode {
      @apply tw-mb-2 tw-inline-flex tw-rounded-lg tw-bg-neutral-100 tw-p-0.5;

      .mode-option {
        // h-10 options + track padding = 44px total hit area.
        @apply tw-h-10 tw-whitespace-nowrap tw-rounded-md tw-border-0 tw-bg-transparent tw-px-3 tw-text-xs tw-font-medium tw-text-neutral-500 tw-transition-colors;

        &.is-active {
          @apply tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-text-neutral-900 tw-shadow-sm;
        }

        &:focus-visible {
          @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
        }
      }
    }

    // Spacing clears the note's 12px pencil overhang (text lives in the note).
    .range-footnote {
      @apply tw-mb-3 tw-mt-1.5;
    }
  }
</style>
